import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdminPlan, useUpdatePlan } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

interface PlanForm {
  name: string;
  stripe_price_id: string;
  price_eur_cents: number;
  max_websites: number;
  scans_per_month_per_website: number;
  max_pages_per_scan: number;
  docs_enabled: number;
  recurring_enabled: number;
  scan_history_months: number;
  sort_order: number;
  is_active: number;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "0.9rem",
  border: "1px solid #d0d0d0",
  borderRadius: "4px",
  boxSizing: "border-box",
};

export function AdminPlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminPlan(planId);
  const updateMutation = useUpdatePlan(planId ?? "");

  const [form, setForm] = useState<PlanForm>({
    name: "",
    stripe_price_id: "",
    price_eur_cents: 0,
    max_websites: 1,
    scans_per_month_per_website: 2,
    max_pages_per_scan: 150,
    docs_enabled: 0,
    recurring_enabled: 0,
    scan_history_months: 1,
    sort_order: 0,
    is_active: 1,
  });
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (data?.plan) {
      setForm({
        name: data.plan.name,
        stripe_price_id: data.plan.stripe_price_id ?? "",
        price_eur_cents: data.plan.price_eur_cents,
        max_websites: data.plan.max_websites,
        scans_per_month_per_website: data.plan.scans_per_month_per_website,
        max_pages_per_scan: data.plan.max_pages_per_scan,
        docs_enabled: data.plan.docs_enabled,
        recurring_enabled: data.plan.recurring_enabled,
        scan_history_months: data.plan.scan_history_months,
        sort_order: data.plan.sort_order,
        is_active: data.plan.is_active,
      });
    }
  }, [data]);

  function setField<K extends keyof PlanForm>(key: K, value: PlanForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(false);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(false);
    try {
      await updateMutation.mutateAsync({
        name: form.name,
        stripe_price_id: form.stripe_price_id || null,
        price_eur_cents: form.price_eur_cents,
        max_websites: form.max_websites,
        scans_per_month_per_website: form.scans_per_month_per_website,
        max_pages_per_scan: form.max_pages_per_scan,
        docs_enabled: form.docs_enabled,
        recurring_enabled: form.recurring_enabled,
        scan_history_months: form.scan_history_months,
        sort_order: form.sort_order,
        is_active: form.is_active,
      });
      setSuccess(true);
    } catch {
      // Error is displayed via updateMutation.isError
    }
  }

  if (isLoading) return <PageSkeleton />;
  if (!data?.plan) return <div className="loading-text">Plan not found.</div>;

  const plan = data.plan;

  return (
    <div className="product-page">
      <button
        type="button"
        className="btn btn--secondary btn--sm mb-3"
        onClick={() => navigate("/admin/plans")}
      >
        ← Back to plans
      </button>

      <h1 className="page-title">Edit: {plan.name}</h1>

      <div className="stat-grid mb-4">
        <div className="stat-item">
          <span className="stat-item__value">{plan.subscriberCount}</span>
          <span className="stat-item__label">Active subscribers</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__value">
            {plan.is_active ? "Active" : "Inactive"}
          </span>
          <span className="stat-item__label">Status</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__value">{plan.id}</span>
          <span className="stat-item__label">Plan ID</span>
        </div>
      </div>

      <section className="product-section">
        <h2 className="section-title">Preview</h2>
        <div className="limits-grid">
          <div className="pricing-preview">
            <h3>Pricing</h3>
            {form.price_eur_cents > 0 ? (
              <>
                <div
                  className="fw-700 mb-1"
                  style={{
                    fontSize: "2.5rem",
                    color: "var(--color-primary)",
                  }}
                >
                  €{(form.price_eur_cents / 100).toFixed(2)}
                </div>
                <p className="text-sm text-muted" style={{ margin: "0.5rem 0 0 0" }}>
                  per month
                </p>
              </>
            ) : (
              <div className="text-muted" style={{ fontSize: "1.1rem" }}>
                Custom pricing
              </div>
            )}
          </div>

          <div className="limit-card">
            <h3>Limits Summary</h3>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: "0.9rem",
              }}
            >
              <li style={{ marginBottom: "0.75rem" }}>
                <strong>{form.max_websites}</strong> website
                {form.max_websites !== 1 ? "s" : ""}
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <strong>{form.scans_per_month_per_website}</strong> scans/month
                per site
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <strong>{form.max_pages_per_scan}</strong> pages per scan
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <strong>{form.scan_history_months}</strong> month
                {form.scan_history_months !== 1 ? "s" : ""} history
              </li>
            </ul>
          </div>

          <div className="limit-card">
            <h3>Included Features</h3>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: "0.9rem",
              }}
            >
              <li className="d-flex items-center gap-2" style={{ marginBottom: "0.75rem" }}>
                <span style={{ fontSize: "1.1rem" }}>
                  {form.docs_enabled === 1 ? "✓" : "×"}
                </span>
                Document scanning
              </li>
              <li className="d-flex items-center gap-2">
                <span style={{ fontSize: "1.1rem" }}>
                  {form.recurring_enabled === 1 ? "✓" : "×"}
                </span>
                Recurring scans
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="product-section">
        <h2 className="section-title">Configuration</h2>

        <form onSubmit={(e) => void handleSave(e)}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 24px",
            }}
          >
            {/* Left column */}
            <div>
              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-name">
                  Name
                </label>
                <input
                  id="plan-name"
                  type="text"
                  style={inputStyle}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-stripe-price">
                  Stripe Price ID
                </label>
                <input
                  id="plan-stripe-price"
                  type="text"
                  style={inputStyle}
                  value={form.stripe_price_id}
                  onChange={(e) => setField("stripe_price_id", e.target.value)}
                  placeholder="price_xxxxxxxxxxxxx"
                />
                <p className="text-xs text-muted mt-1">
                  Copy from Stripe Dashboard → Products → select product →
                  Pricing section. Starts with <code>price_</code>.
                </p>
                {!form.stripe_price_id && (
                  <p className="text-xs text-danger fw-600 mt-1">
                    Checkout will not work without a valid Stripe price ID.
                  </p>
                )}
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-price">
                  Price (EUR cents)
                </label>
                <input
                  id="plan-price"
                  type="number"
                  style={inputStyle}
                  value={form.price_eur_cents}
                  onChange={(e) =>
                    setField(
                      "price_eur_cents",
                      parseInt(e.target.value, 10) || 0,
                    )
                  }
                  min={0}
                />
                <p className="text-xs text-muted mt-1">
                  Display price: €{(form.price_eur_cents / 100).toFixed(2)}/mo
                </p>
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-sort">
                  Sort order
                </label>
                <input
                  id="plan-sort"
                  type="number"
                  style={inputStyle}
                  value={form.sort_order}
                  onChange={(e) =>
                    setField("sort_order", parseInt(e.target.value, 10) || 0)
                  }
                  min={0}
                />
              </div>
            </div>

            {/* Right column */}
            <div>
              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-max-websites">
                  Max websites
                </label>
                <input
                  id="plan-max-websites"
                  type="number"
                  style={inputStyle}
                  value={form.max_websites}
                  onChange={(e) =>
                    setField("max_websites", parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                />
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-scans">
                  Scans per month per website
                </label>
                <input
                  id="plan-scans"
                  type="number"
                  style={inputStyle}
                  value={form.scans_per_month_per_website}
                  onChange={(e) =>
                    setField(
                      "scans_per_month_per_website",
                      parseInt(e.target.value, 10) || 1,
                    )
                  }
                  min={1}
                />
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-pages">
                  Max pages per scan
                </label>
                <input
                  id="plan-pages"
                  type="number"
                  style={inputStyle}
                  value={form.max_pages_per_scan}
                  onChange={(e) =>
                    setField(
                      "max_pages_per_scan",
                      parseInt(e.target.value, 10) || 1,
                    )
                  }
                  min={1}
                />
              </div>

              <div className="mb-3">
                <label className="filter-label" htmlFor="plan-history">
                  Scan history (months)
                </label>
                <input
                  id="plan-history"
                  type="number"
                  style={inputStyle}
                  value={form.scan_history_months}
                  onChange={(e) =>
                    setField(
                      "scan_history_months",
                      parseInt(e.target.value, 10) || 1,
                    )
                  }
                  min={1}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  marginTop: "1.5rem",
                }}
              >
                <h3 style={{ margin: 0 }}>Included Features</h3>

                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <div style={{ marginTop: "2px" }}>
                    <input
                      type="checkbox"
                      checked={form.docs_enabled === 1}
                      onChange={(e) =>
                        setField("docs_enabled", e.target.checked ? 1 : 0)
                      }
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="fw-600" style={{ fontSize: "0.95rem", display: "block" }}>
                      Document scanning
                    </span>
                    <span className="text-xs text-muted" style={{ display: "block", marginTop: "2px" }}>
                      Enable PDF and Office document scanning
                    </span>
                  </div>
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <div style={{ marginTop: "2px" }}>
                    <input
                      type="checkbox"
                      checked={form.recurring_enabled === 1}
                      onChange={(e) =>
                        setField("recurring_enabled", e.target.checked ? 1 : 0)
                      }
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="fw-600" style={{ fontSize: "0.95rem", display: "block" }}>
                      Recurring scans
                    </span>
                    <span className="text-xs text-muted" style={{ display: "block", marginTop: "2px" }}>
                      Allow customers to schedule automated scans
                    </span>
                  </div>
                </label>
              </div>

              <div className="mb-3">
                <label className="d-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active === 1}
                    onChange={(e) =>
                      setField("is_active", e.target.checked ? 1 : 0)
                    }
                  />
                  <span className="text-sm fw-600">
                    Plan active
                  </span>
                </label>
                {form.is_active === 0 && plan.subscriberCount > 0 && (
                  <p className="text-xs text-danger fw-600 mt-1">
                    Cannot deactivate — {plan.subscriberCount} active
                    subscriber(s). Migrate them first.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex items-center gap-3 mt-1">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving…" : "Save changes"}
            </button>

            {success && (
              <span className="text-success text-sm">
                Saved successfully.
              </span>
            )}

            {updateMutation.isError && (
              <span className="text-danger text-sm">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : "Save failed."}
              </span>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
